import React, { useEffect, useState } from "react";
import { Hero } from "components/generic/Hero";
import { Button, Modal } from "react-bootstrap";
import { Formik, Form as FormikForm } from "formik";
import { FormikFormRow } from "components/generic/FormRow";
import * as yup from "yup";
import { INewBookingPropsBase } from "types/INewBookingPropsBase";
import { scrollToTop } from "helpers/ScrollingHelper";
import { useTranslation } from "react-i18next";
import "../../scss/custom.scss";

interface Props extends INewBookingPropsBase {
    currentOdoReading: string | null;
    setOdoReading: (value: string) => void;
    lastOdoReading: number | null; 
    odoReadingCheck: boolean
}

type FormValues = {
    odoReading: string;
};

export const OdometerReading: React.FC<Props> = (props: Props) => {
    const [checked, setChecked] = useState(false);
    const { t } = useTranslation();
    const [showPopup, setShowPopup] = useState(false); 
    const initialValues: FormValues = {
        odoReading: props.currentOdoReading ?? "",
    };
    const schema = yup.object({
        odoReading: yup
            .string()
            .required(t("vehicle_details_odometer_invalid"))
            .matches(/^([1-9][0-9]{0,5})$/, t("vehicle_details_odometer_invalid_range"))
    });

    useEffect(() => scrollToTop(), []);

    const handleSubmit = (values: FormValues, { setSubmitting }: { setSubmitting: (x: boolean) => void }) => {
        const newOdoReading = parseInt(values.odoReading, 10);
        props.setOdoReading(values.odoReading);

        if (props.odoReadingCheck) {  
            if (!checked) {
                if (props.lastOdoReading && newOdoReading < props.lastOdoReading) {
                    setShowPopup(true);
                    setSubmitting(false);
                    return;  // Exit early to prevent further execution
                }
            }
        }
        props.setOdoReading(values.odoReading);
        setSubmitting(false);
        props.goToNextStep();
    };

    const handlePopupClose = () => {
        setShowPopup(false);
        setChecked(true);
        props.setOdoReading(initialValues.odoReading); 

    }

    return (
        <div className="row no-gutters">
            <div className="col-12">
                <Hero heading="vehicle_details_odometer_heading" description="vehicle_details_odometer_description" />
                <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                    {({ isSubmitting }) => (
                        <div>
                            <FormikForm>
                                <div className="form-group">
                                    <FormikFormRow name="odoReading" type="text" placeholder={t("vehicle_details_odometer_placeholder")} />
                                </div>
                                <Button type="submit" variant="primary" className="large-input" disabled={isSubmitting}>
                                    {t("vehicle_details_odometer_continue")}
                                </Button>
                                <div>
                                    {props.odoReadingCheck}
                                </div>
                            </FormikForm>
                        </div>
                    )}
                </Formik>

                {/* Popup for invalid odometer reading */}
                <Modal show={showPopup} onHide={() => setShowPopup(false)} backdrop="static">
                    <Modal.Header closeButton>
                    <Modal.Body>
                        <div className="custom-dialog">
                            <h1>{t("vehicle_details_odometer_mileage_check")}</h1>
                            <h2>{t("vehicle_details_odometer_mileage_check_desc")}</h2>

                            <Button size="lg" variant="primary" onClick={() => 
                                {setShowPopup(false);
                                props.setOdoReading(initialValues.odoReading);  
                                props.goToNextStep()}}>
                                {t("vehicle_details_odometer_continue")}
                            </Button>
                            <p className="link-muted" onClick={handlePopupClose}>
                                {t("vehicle_details_odometer_mileage_check_reenter")}
                            </p>
                        </div>

                    </Modal.Body>
                    </Modal.Header>
                   
                </Modal>
            </div>
        </div>
    );
};
